import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PageHeaderAction } from "@a2z/ui";

/**
 * What a page hands up to the shell's top bar.
 *
 * `ownsHeader` is the important one: it means "this page is NOT drawing its own
 * header, so the bar is the only place its subtitle and action can appear".
 * Without it the desktop bar would render an action the page is also showing
 * next to its own title, and every screen would sprout two Add buttons.
 */
type PageMeta = {
  action: PageHeaderAction | undefined;
  subtitle: string | undefined;
  /**
   * What to call the page in the shell bar, when the NAV cannot.
   *
   * The bar normally names the page from the sidebar item that matched, which
   * is right for a destination the sidebar lists. It is wrong for a screen
   * reached THROUGH one: every page under `/manage/settings` matches the
   * `Settings` item, so a page that hands its header up would have been left
   * with no name of its own anywhere on screen.
   */
  title: string | undefined;
  /**
   * A link rendered before the action in the shell bar. DATA, not a node: the
   * meta is published from an effect keyed on its own scalar fields, and a
   * ReactNode has no stable identity to key on — a node would go stale the
   * moment its label changed.
   */
  secondary:
    | { label: string; icon: ReactNode; to: string; testId?: string }
    | undefined;
  ownsHeader: boolean;
};

type Ctx = {
  meta: PageMeta;
  setMeta: (meta: PageMeta) => void;
};

const EMPTY_META: PageMeta = {
  action: undefined,
  subtitle: undefined,
  title: undefined,
  secondary: undefined,
  ownsHeader: false,
};

const PageActionContext = createContext<Ctx | null>(null);

/**
 * Lets a PAGE publish its primary action to the SHELL's mobile top bar.
 *
 * On a desk the action belongs in the page header, next to the page title, and
 * `PageHeader` renders it there. On a phone this app's title is not in the page
 * at all — the shell's top bar owns it — so a page-level action rendered inside
 * the page floats on a row of its own under a bar that has room for it. The
 * seller portal puts the two together: title at the start of the mobile bar,
 * the compact `+` and the bell at the end.
 *
 * A context rather than prop-drilling because the shell renders ABOVE the
 * router outlet: the page that knows the action is a descendant of the bar that
 * should show it, so the data has to travel back up.
 */
export function PageActionProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<PageMeta>(EMPTY_META);
  const value = useMemo(() => ({ meta, setMeta }), [meta]);
  return (
    <PageActionContext.Provider value={value}>
      {children}
    </PageActionContext.Provider>
  );
}

/** Read by the shell to render the current page's action in its bars. */
export function useMobilePageAction() {
  return useContext(PageActionContext)?.meta.action;
}

/**
 * Read by the shell's DESKTOP bar. Returns nothing unless the page said it is
 * not drawing its own header, so a page with a header keeps its action there.
 */
export function useShellPageMeta(): PageMeta {
  const meta = useContext(PageActionContext)?.meta;
  return meta?.ownsHeader ? meta : EMPTY_META;
}

/**
 * Publishes `action` for as long as the calling page is mounted, and clears it
 * on unmount so a route change cannot leave the previous screen's "Add" sitting
 * in the bar. Identity is compared on the fields that matter rather than by
 * reference, since callers build the object inline on every render.
 */
export function usePublishPageAction(
  action: PageHeaderAction | undefined,
  options: {
    subtitle?: string;
    ownsHeader?: boolean;
    title?: string;
    secondary?: PageMeta["secondary"];
  } = {},
) {
  const ctx = useContext(PageActionContext);
  const setMeta = ctx?.setMeta;
  const { subtitle, ownsHeader = false, title, secondary } = options;
  const key = [
    action?.label ?? "",
    action?.testId ?? "",
    String(action?.disabled ?? false),
    subtitle ?? "",
    String(ownsHeader),
    title ?? "",
    secondary?.label ?? "",
    secondary?.to ?? "",
  ].join("|");

  useEffect(() => {
    if (!setMeta) return;
    setMeta({ action, subtitle, ownsHeader, title, secondary });
    return () => setMeta(EMPTY_META);
    // `action` is intentionally excluded: it is a fresh object literal each
    // render, and `key` already captures every field that changes what shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setMeta]);
}
